import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const SIDEBAR_STYLES = `
  @property --side-angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
  }

  @keyframes side-spin-light {
    to { --side-angle: 360deg; }
  }

  .sb-btn-outer {
    position: relative;
    padding: 2px;
    border-radius: 16px;
    background: transparent;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    margin-bottom: 12px;
    border: 1px solid #e0f2fe;
    background: #fff;
    box-shadow: 0 4px 12px rgba(186, 230, 253, 0.15);
  }

  .sb-btn-ring {
    position: absolute;
    inset: 0;
    border-radius: 16px;
    padding: 2px;
    background: conic-gradient(
      from var(--side-angle, 0deg),
      transparent 0deg,
      #0284c7 60deg,
      #7c3aed 120deg,
      transparent 180deg
    );
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  .sb-btn-outer:hover .sb-btn-ring {
    opacity: 1;
    animation: side-spin-light 1.8s linear infinite;
  }

  .sb-btn-outer:hover {
    background: #fff;
    transform: translateX(8px);
    box-shadow: 0 12px 24px rgba(186, 230, 253, 0.4);
  }

  .sb-btn-active {
    background: #f0f9ff !important;
    border-color: #0284c7 !important;
    box-shadow: 0 8px 20px rgba(186, 230, 253, 0.5) !important;
  }

  .sb-btn-inner {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 18px;
    border-radius: 15px;
    font-size: 15px;
    font-weight: 600;
    color: #475569;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    text-align: left;
  }

  .sb-btn-outer:hover .sb-btn-inner { color: #0369a1; }
  .sb-btn-active .sb-btn-inner { color: #0284c7; }

  @keyframes modal-in {
    from { opacity: 0; transform: translateY(12px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .modal-card { animation: modal-in 0.22s cubic-bezier(0.22, 1, 0.36, 1) both; }
`;

function LogoutModal({ onCancel, onConfirm }) {
  return createPortal(
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(15, 23, 42, 0.4)",
        backdropFilter: "blur(2px)",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
      onClick={onCancel}
    >
      <div
        className="modal-card"
        style={{
          background: "#fff", borderRadius: "16px",
          border: "1px solid #e0f2fe", padding: "36px 32px 28px",
          width: "340px", boxShadow: "0 20px 60px rgba(3,105,161,0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ width: "48px", height: "48px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", marginBottom: "18px" }}>🚪</div>
        <p style={{ fontSize: "16px", fontWeight: 600, color: "#0f172a", margin: "0 0 6px" }}>Log out of Admin?</p>
        <p style={{ fontSize: "13.5px", color: "#94a3b8", lineHeight: "1.5", margin: "0 0 28px" }}>You'll be returned to the login page. Any unsaved changes will be lost.</p>
        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={onCancel} style={{ flex: 1, padding: "10px", background: "#F8FBFF", color: "#475569", border: "1.5px solid #BFDBFE", borderRadius: "8px", fontSize: "14px", fontWeight: 500, cursor: "pointer" }}
            onMouseOver={e => e.currentTarget.style.background = "#EFF6FF"}
            onMouseOut={e => e.currentTarget.style.background = "#F8FBFF"}>
            Cancel
          </button>
          <button onClick={onConfirm} style={{ flex: 1, padding: "10px", background: "#EF4444", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}
            onMouseOver={e => e.currentTarget.style.background = "#DC2626"}
            onMouseOut={e => e.currentTarget.style.background = "#EF4444"}>
            Log out
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function AddAdminModal({ onCancel }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/add-admin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        alert("Admin added successfully!");
        onCancel();
      } else {
        alert(data.message || "Failed to add admin");
      }
    } catch {
      alert("Server error. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "10px 14px", fontSize: "14px",
    color: "#1e3a5f", background: "#F8FBFF",
    border: "1.5px solid #BFDBFE", borderRadius: "8px",
    outline: "none", boxSizing: "border-box",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  };

  return createPortal(
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(15, 23, 42, 0.4)",
        backdropFilter: "blur(2px)",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
      onClick={onCancel}
    >
      <div
        className="modal-card"
        style={{
          background: "#fff",
          borderRadius: "16px",
          border: "1px solid #BFDBFE",
          padding: "36px 32px 28px",
          width: "380px",
          maxHeight: "80vh",      // ✅ LIMIT HEIGHT
          overflowY: "auto",      // ✅ ENABLE SCROLL
          boxShadow: "0 20px 60px rgba(3,105,161,0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ width: "42px", height: "42px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px" }}>🛡️</div>
            <div>
              <p style={{ fontSize: "16px", fontWeight: 600, color: "#0f172a", margin: 0 }}>Add New Admin</p>
              <p style={{ fontSize: "12px", color: "#94a3b8", margin: 0 }}>Grant admin access</p>
            </div>
          </div>
          <button onClick={onCancel} style={{ background: "none", border: "none", fontSize: "18px", color: "#94a3b8", cursor: "pointer", lineHeight: 1 }}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div style={{ marginBottom: "14px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "#4A6FA5", marginBottom: "5px" }}>Full Name</label>
            <input
              type="text" placeholder="John Doe"
              style={inputStyle} value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              onFocus={e => e.target.style.borderColor = "#3B82F6"}
              onBlur={e => e.target.style.borderColor = "#BFDBFE"}
            />
          </div>

          {/* Email */}
          <div style={{ marginBottom: "14px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "#4A6FA5", marginBottom: "5px" }}>Email Address</label>
            <input
              type="email" placeholder="admin@example.com"
              style={inputStyle} value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              onFocus={e => e.target.style.borderColor = "#3B82F6"}
              onBlur={e => e.target.style.borderColor = "#BFDBFE"}
            />
          </div>

          {/* Password */}
          <div style={{ position: "relative", marginBottom: "18px" }}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              style={{ ...inputStyle, paddingRight: "40px" }}
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              onFocus={e => e.target.style.borderColor = "#3B82F6"}
              onBlur={e => e.target.style.borderColor = "#BFDBFE"}
            />

            {/* Eye Icon */}
            <span
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                fontSize: "16px",
                color: "#3B82F6",
                userSelect: "none"
              }}
            >
              {showPassword ? "🙈" : "👁️"}
            </span>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <button type="button" onClick={onCancel} style={{ flex: 1, padding: "10px", background: "#F8FBFF", color: "#475569", border: "1.5px solid #BFDBFE", borderRadius: "8px", fontSize: "14px", fontWeight: 500, cursor: "pointer" }}
              onMouseOver={e => e.currentTarget.style.background = "#EFF6FF"}
              onMouseOut={e => e.currentTarget.style.background = "#F8FBFF"}>
              Cancel
            </button>
            <button type="submit" disabled={loading} style={{ flex: 1, padding: "10px", background: loading ? "#93C5FD" : "#3B82F6", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: 600, cursor: loading ? "not-allowed" : "pointer" }}
              onMouseOver={e => { if (!loading) e.currentTarget.style.background = "#2563EB"; }}
              onMouseOut={e => { if (!loading) e.currentTarget.style.background = "#3B82F6"; }}>
              {loading ? "Adding..." : "Add Admin"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default function Sidebar({ active, setActive, isOpen, onClose }) {
  const navigate = useNavigate();
  const styleInjected = useRef(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);


  useEffect(() => {
    if (!styleInjected.current) {
      const tag = document.createElement("style");
      tag.textContent = SIDEBAR_STYLES;
      document.head.appendChild(tag);
      styleInjected.current = true;
    }
  }, []);

  const menu = [
    {name:"Dashboard",key:"dashboard",icon:"🏠"},
    { name: "Doctors", key: "doctors", icon: "👨‍⚕️" },
    { name: "Appointments", key: "appointments", icon: "📅" },
    { name: "Patients", key: "patients", icon: "🏥" },
  ];

  const confirmLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/login");
  };

  return (
    <>
      {/* Mobile Overlay */}
      <div 
        className={`fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 transition-opacity duration-300 md:hidden ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      <div className={`fixed top-0 left-0 w-64 h-screen bg-[#f8fafc] text-slate-800 flex flex-col p-6 border-r border-[#e0f2fe] shadow-xl z-50 font-['Outfit'] transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
      {/* Logo */}
      <div className="flex items-center gap-3 mb-10 cursor-pointer group" onClick={() => setActive("dashboard")}>
        <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center text-2xl shadow-lg shadow-blue-300 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">⚡</div>
        <div>
          <h2 className="text-xl font-extrabold tracking-tight text-[#0c4a6e] leading-tight">
            Admin <span className="text-blue-600">Portal</span>
          </h2>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Management Center</p>
        </div>
      </div>

      {/* Menu */}
      <div className="flex flex-col flex-1 mt-4">
        <p className="text-xs font-bold text-slate-400 mb-4 uppercase tracking-wider pl-2">Dashboards</p>
        {menu.map((item) => {
          const isActive = active === item.key;
          return (
            <div key={item.key} className={`sb-btn-outer ${isActive ? "sb-btn-active" : ""}`} onClick={() => setActive(item.key)}>
              <div className="sb-btn-ring" />
              <button className="sb-btn-inner">
                <span className="text-xl">{item.icon}</span>
                {item.name}
              </button>
            </div>
          );
        })}
      </div>

      {/* Bottom */}
      <div className="mt-auto pt-6 border-t border-[#e0f2fe] flex flex-col gap-3">
        {/* Add Admin Button */}
        <button
          onClick={() => setShowAddAdminModal(true)}
          className="w-full text-center text-sm font-bold py-4 rounded-xl transition-all duration-300 bg-blue-50 text-blue-600 hover:bg-blue-500 hover:text-white border border-blue-100 shadow-sm"
        >
          🛡️ Add Admin
        </button>

        {/* Logout Button */}
        <button
          onClick={() => setShowLogoutModal(true)}
          className="w-full text-center text-sm font-bold py-4 rounded-xl transition-all duration-300 bg-red-50 text-red-500 hover:bg-red-500 hover:text-white border border-red-100 shadow-sm"
        >
          🚪 Leave Admin
        </button>
      </div>

      {showLogoutModal && <LogoutModal onCancel={() => setShowLogoutModal(false)} onConfirm={confirmLogout} />}
      {showAddAdminModal && <AddAdminModal onCancel={() => setShowAddAdminModal(false)} />}
    </div>
    </>
  );
}